import { ReactNode } from "react";
import {
  Control,
  FieldErrors,
  FieldPath,
  UseFormRegister,
} from "react-hook-form";
import CXOneConfigForm from "../forms/CXOneConfigForm";
import Connector, {
  ConnectorSecret,
  connectorSecretSchema,
  EditDefaultValuesGetter,
  IConnectorRow,
} from "./Connector";
import { z } from "zod";
import { ConnectorWizardFormValues } from "../forms/ConnectorWizard";

export interface CXoneConnectorRow extends Omit<
  IConnectorRow,
  "connectorDetails"
> {
  connectorDetails: {
    AccessKey: ConnectorSecret;
    SecretKey: ConnectorSecret;
    ClientId: ConnectorSecret;
    ClientSecret: ConnectorSecret;
  };
}

export const cxOneSchema = z
  .object({
    accessKey: connectorSecretSchema({
      required: true,
      requiredMessage: "cxoneConnector:accessKeyRequired",
    }),
    secretKey: connectorSecretSchema({
      required: true,
      requiredMessage: "cxoneConnector:secretKeyRequired",
    }),
    clientId: connectorSecretSchema({
      required: false,
    }),
    clientSecret: connectorSecretSchema({
      required: false,
    }),
  })
  .superRefine((data, context) => {
    if (!!data.clientId && !!data.clientSecret) {
      return;
    }
    if (data.clientId) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "cxoneConnector:clientSecretMustBeSpecifiedWithClientId",
        path: ["clientSecret"],
      });
    }
    if (data.clientSecret) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "cxoneConnector:clientIdMustBeSpecifiedWithClientSecret",
        path: ["clientId"],
      });
    }
  });

export type CXoneFormValues = z.infer<typeof cxOneSchema>;

export default class CXoneConnector implements Connector<
  CXoneFormValues,
  CXoneConnectorRow
> {
  public static readonly Type = "CXone";

  public get type(): string {
    return CXoneConnector.Type;
  }

  public get defaultValues(): CXoneFormValues {
    return {
      accessKey: { value: "", isSensitive: false, hasValue: false },
      secretKey: { value: "", isSensitive: true, hasValue: false },
      clientId: { value: "", isSensitive: false, hasValue: false },
      clientSecret: { value: "", isSensitive: true, hasValue: false },
    };
  }

  public getEditDefaultValues: EditDefaultValuesGetter<
    CXoneFormValues,
    CXoneConnectorRow
  > = (connector?: CXoneConnectorRow): CXoneFormValues => {
    return {
      accessKey: connector?.connectorDetails?.AccessKey ?? {
        value: "",
        isSensitive: false,
        hasValue: false,
      },
      secretKey: connector?.connectorDetails?.SecretKey ?? {
        value: "",
        isSensitive: true,
        hasValue: false,
      },
      clientId: connector?.connectorDetails?.ClientId ?? {
        value: "",
        isSensitive: false,
        hasValue: false,
      },
      clientSecret: connector?.connectorDetails?.ClientSecret ?? {
        value: "",
        isSensitive: true,
        hasValue: false,
      },
    };
  };

  public get fields(): FieldPath<ConnectorWizardFormValues>[] {
    return [
      ...cxOneSchema._def.schema
        .keyof()
        .options.map((key) => `${CXoneConnector.Type}.${key}`),
    ];
  }

  public renderFormFields(
    register: UseFormRegister<ConnectorWizardFormValues>,
    control: Control<ConnectorWizardFormValues>,
    errors: FieldErrors<ConnectorWizardFormValues>,
  ): ReactNode {
    return (
      <CXOneConfigForm
        defaultValues={this.defaultValues}
        register={register}
        control={control}
        errors={errors}
      />
    );
  }
}
